import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Clock, FileText, Landmark, Loader2, ArrowLeft, Zap, Globe, MapPin } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { bankImage } from "@/lib/images";
import { setSeo, setJsonLd, clearJsonLd, resetSeo } from "@/lib/seo";
import { getDisplayConfig, enabledFields } from "@/lib/displayConfig";
import { timelineText, TIMELINE_QUALIFIER } from "@/lib/timeline";
import { useTranslation } from "react-i18next";

export default function BankDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [factFields, setFactFields] = useState([]);
  const { t, i18n } = useTranslation();
  // Per-bank strings: reviewed translation when served, otherwise the live English.
  const tb = (suffix, en) => t(`bank.${slug}.${suffix}`, { defaultValue: en });

  useEffect(() => {
    setLoading(true);
    api.get(`/bank/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
    getDisplayConfig().then((cfg) => setFactFields(enabledFields(cfg, "snippet_bank")));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const fee = formatUSD(data.fee);
    setSeo({
      title: t(`seo.bank.${data.slug}.title`, { name: data.name, defaultValue: `Open a ${data.name} Corporate Account — Fees & Requirements | UNICORP` }),
      description: t(`seo.bank.${data.slug}.desc`, { name: data.name, fee, defaultValue: `Open a ${data.name} corporate bank account from ${fee}, fully managed and remote. See requirements, supported countries and FAQs with UNICORP.` }),
      image: bankImage(data.type),
      url: window.location.href,
    });
    if (data.faqs?.length) {
      setJsonLd("bank-faq-schema", {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: data.faqs.map((f, i) => ({ "@type": "Question", name: tb(`faq${i + 1}q`, f.q), acceptedAnswer: { "@type": "Answer", text: tb(`faq${i + 1}a`, f.a) } })),
      });
    }
    return () => { clearJsonLd("bank-faq-schema"); resetSeo(); };
  }, [data, i18n.language, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;
  if (notFound || !data) return (
    <div className="min-h-screen"><Navbar /><div className="max-w-3xl mx-auto px-5 pt-40 pb-24 text-center"><h1 className="font-heading text-3xl text-primary">{t("bank_ui.notFound")}</h1><Button className="mt-6 rounded-sm bg-primary text-primary-foreground" onClick={() => navigate("/banks")}>{t("bank_ui.browseAll")}</Button></div><Footer /></div>
  );

  const start = () => navigate(`/start?pkg=bank_${data.slug}`);
  const isDigital = data.type === "Digital";
  const cName = (c) => t(`country_name.${c.slug}`, { defaultValue: c.name });
  const tl = (data.time_to_open || "").trim() || t("bank_ui.timelineDefault");
  const currencies = data.currencies === "Multi-currency (30+ currencies)" ? t("bank_ui.currMulti") : data.currencies;

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain overflow-hidden border-b border-border pt-28">
        <div className="absolute inset-0"><img src={bankImage(data.type)} alt={data.name} className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/92 to-background" /></div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-3 gap-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2">
            <button onClick={() => navigate("/banks")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5"><ArrowLeft className="h-4 w-4" /> {t("bank_ui.allBanks")}</button>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-600"><Landmark className="h-3.5 w-3.5" /> {t("bank_ui.corporateBanking")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary">{data.name}</h1>
            {data.overview && <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-2xl">{tb("overview", data.overview)}</p>}
            <div className="mt-6 flex flex-wrap gap-6 text-sm">
              <span className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isDigital ? "text-blue-700 bg-blue-100" : "text-slate-700 bg-slate-100"}`}>{isDigital ? <Zap className="h-3.5 w-3.5" /> : <Landmark className="h-3.5 w-3.5" />} {isDigital ? t("bank_ui.fintechAccount") : t("bank_ui.bankAccount")}</span>
              <span className="flex items-center gap-2 text-muted-foreground"><Globe className="h-4 w-4 text-orange-600" /> {currencies}</span>
              <span className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 text-orange-600" /> {tl}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="rounded-2xl border border-border bg-card p-6 card-soft h-fit lg:sticky lg:top-28" data-testid="bank-fee-card">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bank_ui.supportFee")}</p>
            <p className="mt-1 font-heading text-4xl tracking-tight text-primary">{formatUSD(data.fee)}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t(isDigital ? "bank_ui.feeNoteProvider" : "bank_ui.feeNoteBank")}</p>
            <Button onClick={start} className="mt-5 w-full rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="bank-start">{t("bank_ui.order")} <ArrowRight className="h-4 w-4 ml-1" /></Button>
            <p className="mt-3 text-[11px] text-muted-foreground text-center">{t("bank_ui.noInc")}</p>
          </motion.div>
        </div>
      </section>

      {/* Key facts (admin-controlled snippet fields) */}
      {(() => {
        const factVal = (k) => {
          if (k === "fee") return formatUSD(data.fee);
          if (k === "type") return t(`bank_ui.type.${data.type}`, { defaultValue: data.type });
          if (k === "country_count") return t("bank_ui.jurisdictions", { count: data.countries?.length || 0 });
          return data[k];
        };
        const tiles = factFields.map((f) => ({ ...f, val: factVal(f.key) })).filter((f) => f.val);
        if (!tiles.length) return null;
        return (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16" data-testid="bank-keyfacts">
            <h2 className="font-heading text-2xl text-primary mb-6">{t("bank_ui.keyFacts")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {tiles.map((f) => (
                <div key={f.key} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`bank-fact-${f.key}`}>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t(`bank_ui.factLabel.${f.key}`, { defaultValue: f.label })}</p>
                  <p className="mt-1.5 font-heading text-lg text-primary">{f.val}</p>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {data.intro?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl text-primary">{t("bank_ui.about", { name: data.name })}</h2>
            <div className="mt-5 space-y-4">{data.intro.map((p, i) => <p key={i} className="text-muted-foreground leading-relaxed">{tb(`intro${i + 1}`, p)}</p>)}</div>
          </div>
        </section>
      )}

      {(data.features?.length > 0 || data.requirements?.length > 0) && (
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-10">
        {data.features?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-7 card-soft">
          <h2 className="font-heading text-2xl text-primary">{t("bank_ui.whatsIncluded")}</h2>
          <ul className="mt-5 space-y-3">{data.features.map((f, i) => (<li key={f} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-orange-100 shrink-0"><Check className="h-3 w-3 text-orange-700" /></span>{tb(`feat${i + 1}`, f)}</li>))}</ul>
        </div>
        )}
        {data.requirements?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-7 card-soft">
          <h2 className="font-heading text-2xl text-primary">{t("bank_ui.requirements")}</h2>
          <ul className="mt-5 space-y-3">{data.requirements.map((r, i) => (<li key={r} className="flex items-start gap-2.5 text-sm text-slate-700"><FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />{tb(`req${i + 1}`, r)}</li>))}</ul>
        </div>
        )}
      </section>
      )}

      {data.process?.length > 0 && (
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <h2 className="font-heading text-2xl text-primary mb-8">{t("bank_ui.howItWorks")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.process.map((p, i) => (
            <div key={p.step} className="rounded-xl border border-border bg-card p-6 card-soft">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground font-mono text-sm">{i + 1}</span>
              <h3 className="mt-4 font-heading text-lg text-primary">{tb(`proc${i + 1}s`, p.step)}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{tb(`proc${i + 1}d`, p.detail)}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {data.countries?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-heading text-2xl text-primary mb-8">{t("bank_ui.availableIn")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.countries.map((c) => (
              <button key={c.slug} disabled={!c.has_page} onClick={() => c.has_page && navigate(`/company-formation/${c.slug}`)}
                className={`rounded-xl border border-border bg-card p-5 card-soft flex items-center gap-3 text-left ${c.has_page ? "hover:border-orange-300 hover:shadow-sm transition-all" : "opacity-70 cursor-default"}`}
                data-testid={`bank-country-${c.slug}`}>
                <span className="grid place-items-center h-9 w-9 rounded-sm bg-secondary text-orange-600"><MapPin className="h-4 w-4" /></span>
                <span className="text-sm text-primary">{cName(c)}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {data.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-heading text-2xl sm:text-3xl text-primary mb-6">{t("bank_ui.faqTitle")}</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="bank-faqs">
            {data.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-foreground hover:text-orange-700 hover:no-underline">{tb(`faq${i + 1}q`, f.q)}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{tb(`faq${i + 1}a`, f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-2xl bg-primary text-primary-foreground px-8 py-12 text-center grain">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">{t("bank_ui.readyTitle", { name: data.name })}</h2>
          <p className="mt-3 text-primary-foreground/70">{t("bank_ui.readySub", { fee: formatUSD(data.fee) })}</p>
          <Button size="lg" className="mt-7 rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-8" onClick={start} data-testid="bank-cta">{t("bank_ui.order")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
